import React, { useRef, useState } from "react";
import "./chatbotbody.scss";
import Entermessagebox from "../entermessagebox";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// Removed unused Input and Button imports
import { initializeChat } from "../../../store/slices/projectSlice";

export default function Chatbotbody({
  userChatById,
  isKnowledgeChat,
  tempUserData,
  setTempUserData,
  chatWithBot,
  setChatWithBot,
  selectedUser,
  BgColor,
}) {
  const { selectedProject } = useSelector((state) => state.project);
  const [loading, setLoading] = useState(false);

  const [userInfo, setUserInfo] = useState({});
  const [botImageSrc, setBotImageSrc] = useState("/bot.svg");
  const [welcomeImageSrc, setWelcomeImageSrc] = useState("/college_photo.jpg");

  const baseColor = selectedProject?.base_color;
  const isGradient = typeof baseColor === 'string' && baseColor.toLowerCase().includes("gradient");
  const scrollbarStyle = isGradient
    ? `background-image: ${baseColor} !important; background-color: transparent !important;`
    : `background-color: ${baseColor || "#888"} !important; background-image: none !important;`;

  React.useEffect(() => {
    let botObjUrl = null;
    let welcomeObjUrl = null;

    if (selectedProject?.bot_image instanceof Blob || selectedProject?.bot_image instanceof File) {
      botObjUrl = URL.createObjectURL(selectedProject.bot_image);
      setBotImageSrc(botObjUrl);
    } else if (typeof selectedProject?.bot_image === "string" && selectedProject?.bot_image) {
      setBotImageSrc(selectedProject.bot_image);
    } else {
      setBotImageSrc("/bot.svg");
    }

    if (selectedProject?.welcome_image instanceof Blob || selectedProject?.welcome_image instanceof File) {
      welcomeObjUrl = URL.createObjectURL(selectedProject.welcome_image);
      setWelcomeImageSrc(welcomeObjUrl);
    } else if (typeof selectedProject?.welcome_image === "string" && selectedProject?.welcome_image && selectedProject?.welcome_image !== "null") {
      setWelcomeImageSrc(selectedProject.welcome_image);
    } else {
      setWelcomeImageSrc("");
    }

    return () => {
      if (botObjUrl) URL.revokeObjectURL(botObjUrl);
      if (welcomeObjUrl) URL.revokeObjectURL(welcomeObjUrl);
    };
  }, [selectedProject?.bot_image, selectedProject?.welcome_image]);

  React.useEffect(() => {
    if (selectedUser) {
      let formData = selectedUser.form_data || {};
      if (typeof formData === 'string') {
        try {
          formData = JSON.parse(formData);
        } catch (e) {
          console.error("Error parsing form_data", e);
          formData = {};
        }
      }
      setUserInfo({
        ...selectedUser,
        ...formData
      });
    }
  }, [selectedUser]);

  const [errors, setErrors] = useState({});
  const { pathname } = useLocation();

  const getParsedFormConfig = () => {
    let config = selectedProject?.form_config;
    if (typeof config === 'string') {
      try { config = JSON.parse(config); } catch (e) { config = []; }
    }
    return Array.isArray(config) ? config : [];
  };
  const { route } = useParams();
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const [animationStep, setAnimationStep] = useState(0);

  const hasFormConfig = (() => {
    const config = getParsedFormConfig();
    return config.length > 0;
  })();

  React.useEffect(() => {
    if (tempUserData === "" && hasFormConfig) {
      setAnimationStep(0);

      const timer1 = setTimeout(() => setAnimationStep(1), 300);
      const timer2 = setTimeout(() => setAnimationStep(2), 1200);
      const timer3 = setTimeout(() => setAnimationStep(3), 1800);
      const timer4 = setTimeout(() => setAnimationStep(4), 2400);
      const timer5 = setTimeout(() => setAnimationStep(5), 3500);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
        clearTimeout(timer5);
      };
    }
  }, [pathname, tempUserData, hasFormConfig]);

  const scrollToBottom = React.useCallback(() => {
    if (pathname.includes("generalchat") || pathname.includes("/chat/")) {
      messagesEndRef?.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    } else {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  }, [pathname]);

  React.useEffect(() => {
    scrollToBottom();
  }, [chatWithBot, userChatById, animationStep, tempUserData, scrollToBottom]);

  const renderWithLinks = (text) => {
    if (!text) return text;

    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const processInline = (line, lineKey) => {
      const parts = line.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s]+)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.match(urlRegex)) {
          return (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer"
              style={{ color: '#0066cc', textDecoration: 'underline' }}>
              {part}
            </a>
          );
        }
        return part;
      });
    };

    const lines = text.split('\n');
    return lines.map((line, index) => {
      const trimmed = line.trim();

      if (trimmed === '') {
        return <div key={index} style={{ height: '6px' }} />;
      }

      const headingMatch = trimmed.match(/^#{1,3}\s+(.+)/);
      if (headingMatch) {
        return (
          <div key={index} style={{ fontWeight: 'bold', marginBottom: '4px', marginTop: '4px' }}>
            {processInline(headingMatch[1], index)}
          </div>
        );
      }

      if (/^[•\-*]\s/.test(trimmed)) {
        const content = trimmed.replace(/^[•\-*]\s/, '');
        return (
          <div key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '2px' }}>
            <span style={{ marginRight: '6px', flexShrink: 0 }}>•</span>
            <span>{processInline(content, index)}</span>
          </div>
        );
      }

      return (
        <div key={index} style={{ marginBottom: '2px' }}>
          {processInline(trimmed, index)}
        </div>
      );
    });
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setUserInfo({ ...userInfo, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validForm = () => {
    let Fromvlid = true;
    let errors = {};
    const config = getParsedFormConfig();
    const isArrayConfig = config.length > 0;

    const isFieldEnabled = (key, label) => {
      if (isArrayConfig) {
        return config.some(f => {
          const fName = f.name || f.label?.toLowerCase().replace(/[^a-z0-9]/g, '_');
          return fName === key || (label && f.label === label);
        });
      }
      return config?.[key] !== false;
    };

    const isFieldRequired = (key, label) => {
      if (isArrayConfig) {
        const field = config.find(f => {
          const fName = f.name || f.label?.toLowerCase().replace(/[^a-z0-9]/g, '_');
          return fName === key || (label && f.label === label);
        });
        return field?.required || (field?.label && field.label.includes('*'));
      }
      return false;
    };

    if (isFieldEnabled("name", "Name*")) {
      if (isFieldRequired("name", "Name*") && !userInfo.name?.trim()) {
        Fromvlid = false;
        errors["name"] = "fill this field";
      }
    }

    if (isFieldEnabled("phone", "Mobile Number*")) {
      const required = isFieldRequired("phone", "Mobile Number*");
      if (required && !userInfo.phone?.trim()) {
        Fromvlid = false;
        errors["phone"] = "fill this field";
      } else if (userInfo.phone?.trim() && !/^[0-9]{10}$/.test(userInfo.phone.trim())) {
        Fromvlid = false;
        errors["phone"] = "Please enter valid 10-digit Phone No.";
      }
    }

    if (isFieldEnabled("email", "Email ID*")) {
      const required = isFieldRequired("email", "Email ID*");
      if (required && !userInfo.email?.trim()) {
        Fromvlid = false;
        errors["email"] = "fill this field";
      } else if (userInfo.email?.trim()) {
        let lastAtPos = userInfo.email.lastIndexOf("@");
        let lastDotPos = userInfo.email.lastIndexOf(".");
        if (
          !(
            lastAtPos < lastDotPos &&
            lastAtPos > 0 &&
            userInfo.email.indexOf("@@") === -1 &&
            lastDotPos > 2 &&
            userInfo.email.length - lastDotPos > 2
          )
        ) {
          Fromvlid = false;
          errors["email"] = "Please enter valid Email ID";
        }
      }
    }

    if (isFieldEnabled("course_interested", "Course Interested*")) {
      if (isFieldRequired("course_interested", "Course Interested*") && !userInfo.course_interested?.trim()) {
        Fromvlid = false;
        errors["course_interested"] = "fill this field";
      }
    }

    if (isArrayConfig) {
      config.forEach(field => {
        const fieldLabel = field.label || "";
        const fieldName = field.name || fieldLabel.toLowerCase().replace(/[^a-z0-9]/g, '_');
        if (['name', 'phone', 'email', 'course_interested'].includes(fieldName)) return;

        if (field.required || (field.label && field.label.includes('*'))) {
          if (!userInfo[fieldName]?.trim()) {
            Fromvlid = false;
            errors[fieldName] = "fill this field";
          }
        }
      });
    }

    setErrors(errors);
    return Fromvlid;
  };

  const handleOnSubmit = () => {
    if (!validForm()) return;
    let body = {
      ...userInfo,
    };
    dispatch(initializeChat(body))
      .unwrap()
      .then((res) => {
        if (!res?.data?.success) return;
        setTempUserData(res?.data?.data);
      });
  };

  const handleQuickReply = (label, response) => {
    let finalResponse = response;

    if (response && (response.startsWith('http') || response.startsWith('www.'))) {
      const url = response.startsWith('www.') ? `https://${response}` : response;
      finalResponse = `Here is the link you requested:\n${url}`;
    }

    setChatWithBot((prev) => [...prev, label, finalResponse]);

    setTimeout(() => {
      scrollToBottom();
    }, 0);
  };

  return (
    <div className="chatbot-body-container">
      <style>{`
        .chatbot-body-container .all-message-alignment {
          scrollbar-color: auto !important;
        }
        .chatbot-body-container .all-message-alignment::-webkit-scrollbar-thumb {
          ${scrollbarStyle}
          border-radius: 10px;
        }
        .chatbot-body-container .all-message-alignment::-webkit-scrollbar-thumb:hover {
          opacity: 0.85;
          ${scrollbarStyle}
          border-radius: 10px;
        }
      `}</style>
      <div
        className={`chat-bot-body-alignment`}
        style={{
          background:
            tempUserData === "" && hasFormConfig
              ? "#0003"
              : "",
        }}
      >
        {tempUserData === "" && hasFormConfig ? (
          <div className="all-message-alignment pre-form-height">
            {animationStep === 1 && (
              <div className="message-with-avatar animate-fade-in">
                <div className="avatar-spacer"></div>
                <div className="message typing-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            )}

            {animationStep >= 2 && (
              <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span className="message-sender-label bot-label" style={{ color: selectedProject?.base_color?.includes('gradient') ? '#E53935' : (selectedProject?.base_color || '#E53935'), display: 'block', marginBottom: '4px' }}>
                  {selectedProject?.bot_name || "AI Assistant"}
                </span>
                <div className="message welcome-bubble">
                  <span className="welcome-title">{selectedProject?.welcome_title || "Welcome to D.Y. Patil Technical Campus!"}</span>
                </div>
              </div>
            )}

            {animationStep === 4 && (
              <div className="animate-fade-in" style={{ marginTop: '8px' }}>
                <div className="message typing-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            )}

            {animationStep >= 5 && (
              <div className="animate-fade-in" style={{ padding: '0 4px', marginTop: '8px' }}>
                <div className="message form-bubble">
                  <p className="form-intro">
                    Before we proceed further, may I know your contact details so that if the connection gets interrupted we can get back to you.
                  </p>

                  <div className="inline-form">
                    {(() => {
                      const fieldsToShow = getParsedFormConfig();

                      return fieldsToShow.map((field, idx) => {
                        const fieldLabel = field.label || "";
                        const fieldName = field.name || fieldLabel.toLowerCase().replace(/[^a-z0-9]/g, '_');
                        const inputType = field.type || "text";

                        return (
                          <div className="form-field" key={idx}>
                            <label>{field.label}</label>
                            {inputType === "select" ? (
                              <select
                                name={fieldName}
                                value={userInfo?.[fieldName] || ""}
                                onChange={handleOnChange}
                                className={errors?.[fieldName] ? "error-field" : ""}
                              >
                                <option value="" disabled>Select {field.label}</option>
                                {(field.options || []).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : inputType === "textarea" ? (
                              <textarea
                                name={fieldName}
                                placeholder={field.placeholder || field.label}
                                value={userInfo?.[fieldName] || ""}
                                onChange={handleOnChange}
                                rows={3}
                                className={errors?.[fieldName] ? "error-field" : ""}
                              />
                            ) : (
                              <input
                                type={inputType}
                                placeholder={field.placeholder || field.label}
                                name={fieldName}
                                value={userInfo?.[fieldName] || ""}
                                onChange={handleOnChange}
                                className={errors?.[fieldName] ? "error-field" : ""}
                              />
                            )}
                            {errors?.[fieldName] && <span className="error-text">{errors[fieldName]}</span>}
                          </div>
                        );
                      });
                    })()}

                    <button
                      className="proceed-btn"
                      onClick={handleOnSubmit}
                      style={{ background: selectedProject?.base_color || '#6B7280' }}
                    >
                      {loading ? "Processing..." : "Click to Proceed"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef}></div>
          </div>
        ) : (
          <>
            <div
              ref={chatContainerRef}
              className={`all-message-alignment ${(pathname.includes("generalchat") || pathname.includes("/chat/"))
                ? "chat-bot-body-alignment-full-height"
                : ""
                }`}
            >
              {/* Welcome Message — Line 1 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span className="message-sender-label bot-label" style={{ color: selectedProject?.base_color?.includes('gradient') ? '#E53935' : (selectedProject?.base_color || '#E53935'), display: 'block', marginBottom: '4px' }}>
                  {selectedProject?.bot_name || "AI Assistant"}
                </span>
                <div className="message welcome-bubble">
                  <span className="welcome-title">{(selectedProject?.welcome_title && selectedProject.welcome_title !== "null") ? selectedProject.welcome_title : "Welcome! How can I assist you?"}</span>
                  <span className="timestamp" style={{ display: 'block', marginTop: '4px' }}>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>

              {/* Welcome Message — Line 2 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '8px' }}>
                <div className="message welcome-bubble" style={{ borderRadius: '20px' }}>
                  <span className="welcome-subtitle">How can I help you today?</span>
                  <span className="timestamp" style={{ display: 'block', marginTop: '4px' }}>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>

              {selectedProject?.quick_links?.length > 0 && (
                <div className="quick-replies-container">
                  {selectedProject.quick_links.map((item, index) => (
                    <button
                      key={index}
                      className="quick-reply-btn"
                      onClick={() => handleQuickReply(item.label, item.url || item.response)}
                      style={{ borderColor: selectedProject?.base_color?.includes("gradient") ? '#79376f' : (selectedProject?.base_color || '#79376f') }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}

              {route === "chat-history"
                ? userChatById?.map((item, index) => {
                  return (
                    <React.Fragment key={index}>
                      <div className="message-right">
                        <span className="message-sender-label user-label">You</span>
                        <div className="message">
                          <span>{item?.question}</span>
                          <span className="timestamp">{new Date(item?.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                      <div>
                        <span className="message-sender-label bot-label" style={{ color: selectedProject?.base_color?.includes('gradient') ? '#E53935' : (selectedProject?.base_color || '#E53935') }}>{selectedProject?.bot_name || "AI Assistant"}</span>
                        <div className="message">
                          <span>{renderWithLinks(item?.answer)}</span>
                          <span className="timestamp">{new Date(item?.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
                : chatWithBot?.map((item, index) => {
                  return (
                    <React.Fragment key={index}>
                      {index % 2 === 0 ? (
                        <div className="message-right">
                          <span className="message-sender-label user-label">You</span>
                          <div className="message">
                            <span>{item}</span>
                            <span className="timestamp">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="message-sender-label bot-label" style={{ color: selectedProject?.base_color?.includes('gradient') ? '#E53935' : (selectedProject?.base_color || '#E53935') }}>{selectedProject?.bot_name || "AI Assistant"}</span>
                          <div className="message">
                            <span>{renderWithLinks(item)}</span>
                            <span className="timestamp">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              {loading && (
                <div className="message typing-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              )}

              <div ref={messagesEndRef}></div>
            </div>
          </>
        )}
      </div>
      {!pathname.includes("chat-history") && (tempUserData !== "" || !hasFormConfig) && (
        <Entermessagebox
          isKnowledgeChat={isKnowledgeChat}
          setChatWithBot={setChatWithBot}
          chatWithBot={chatWithBot}
          tempUserData={tempUserData}
          loading={loading}
          setLoading={setLoading}
          scrollToBottom={scrollToBottom}
        />
      )}
    </div>
  );
}
